/** Saudi / Maghreb plate digit & letter maps (shared with add-car forms). */

export const convertArabicToEnglish = (value: string = "") => {
  if (typeof value !== "string") return "";
  const arabicToEnglishMap: { [key: string]: string } = {
    "٠": "0",
    "١": "1",
    "٢": "2",
    "٣": "3",
    "٤": "4",
    "٥": "5",
    "٦": "6",
    "٧": "7",
    "٨": "8",
    "٩": "9",
  };
  return value.replace(/[٠-٩]/g, (d) => arabicToEnglishMap[d] || d);
};

export const convertEnglishToArabic = (value: string = "") => {
  if (typeof value !== "string") return "";
  const englishToArabicMap: { [key: string]: string } = {
    "0": "٠",
    "1": "١",
    "2": "٢",
    "3": "٣",
    "4": "٤",
    "5": "٥",
    "6": "٦",
    "7": "٧",
    "8": "٨",
    "9": "٩",
  };
  return value.replace(/[0-9]/g, (d) => englishToArabicMap[d] || d);
};

const arabicToEnglishMap: Record<string, string> = {
  أ: "A",
  ب: "B",
  ح: "J",
  د: "D",
  ر: "R",
  س: "S",
  ص: "X",
  ط: "T",
  ق: "G",
  ك: "K",
  ل: "L",
  م: "Z",
  ن: "N",
  ه: "H",
  "هـ": "H",
  و: "U",
  ى: "V",
  ع: "E",
};

export const convertArabicToEnglishletters = (arabicText: string) => {
  if (arabicText === "هـ") return "H";
  return arabicText
    .split("")
    .map((char) => arabicToEnglishMap[char] || char)
    .join("");
};

const englishToArabicMap = Object.entries(arabicToEnglishMap).reduce(
  (acc, [arabic, english]) => {
    acc[english] = arabic;
    return acc;
  },
  {} as Record<string, string>
);

export const convertEnglishToArabicLetters = (englishText: string) => {
  return englishText
    .split("")
    .map((char) => englishToArabicMap[char] || char)
    .join("");
};

const AR_NUM = /[٠-٩]/;
const AR_LETTER = /[\u0600-\u06FF]/;

export type SaudiPlateRows = {
  arNums: string;
  arLetters: string;
  enNums: string;
  enLetters: string;
};

function padDigitsPlaceholder(s: string, empty: string) {
  if (!s) return empty;
  return s;
}

/**
 * Build Arabic (top) + Latin (bottom) rows for Saudi-style plate display.
 * Optional letter1–3 match add-car form order (L1, L2, L3); English row shows L3 L2 L1 with spaces.
 */
export function buildSaudiPlateRows(
  plateNumbers: string | number | null | undefined,
  opts?: {
    letter1?: string;
    letter2?: string;
    letter3?: string;
    plateLetters?: string | null;
  }
): SaudiPlateRows {
  const rawNum = String(plateNumbers ?? "").trim().replace(/\s+/g, "");
  let arNums = "";
  let enNums = "";
  if (!rawNum) {
    arNums = "····";
    enNums = "····";
  } else if (AR_NUM.test(rawNum)) {
    arNums = padDigitsPlaceholder(rawNum, "····");
    enNums = convertArabicToEnglish(rawNum) || "····";
  } else {
    const latin = rawNum.replace(/\D/g, "").slice(0, 4);
    enNums = latin || "····";
    arNums = convertEnglishToArabic(latin) || "····";
  }

  const l1 = (opts?.letter1 ?? "").trim();
  const l2 = (opts?.letter2 ?? "").trim();
  const l3 = (opts?.letter3 ?? "").trim();
  const hasTriple = l1 || l2 || l3;

  let arLetters = "· · ·";
  let enLetters = "· · ·";

  const slotAr = (ch: string) => {
    if (!ch) return "·";
    return AR_LETTER.test(ch) ? ch : convertEnglishToArabicLetters(ch);
  };
  const slotEn = (ch: string) => {
    if (!ch) return "·";
    return AR_LETTER.test(ch) ? convertArabicToEnglishletters(ch) : ch;
  };

  if (hasTriple) {
    arLetters = `${slotAr(l1)} ${slotAr(l2)} ${slotAr(l3)}`.trim();
    enLetters = `${slotEn(l3)} ${slotEn(l2)} ${slotEn(l1)}`.trim();
  } else {
    const rawLetters = String(opts?.plateLetters ?? "").replace(/\s+/g, "");
    if (rawLetters.length >= 1) {
      if (AR_LETTER.test(rawLetters)) {
        const chars = Array.from(rawLetters);
        arLetters = chars.join(" ");
        const lat = chars.map((c) => convertArabicToEnglishletters(c)).filter(Boolean);
        enLetters = [...lat].reverse().join(" ");
      } else {
        const compact = rawLetters.toUpperCase();
        if (/^[A-Z]{3}$/.test(compact.slice(0, 3))) {
          const c0 = compact[0];
          const c1 = compact[1];
          const c2 = compact[2];
          arLetters = `${convertEnglishToArabicLetters(c2)} ${convertEnglishToArabicLetters(
            c1
          )} ${convertEnglishToArabicLetters(c0)}`.trim();
          enLetters = `${c0} ${c1} ${c2}`;
        } else {
          arLetters = Array.from(compact).join(" ");
          enLetters = compact;
        }
      }
    }
  }

  return { arNums, arLetters, enNums, enLetters };
}
